import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { SendIcon, CheckCircleIcon, ArrowLeftIcon } from 'lucide-react';
import { type Role } from '../data/surveyData';
interface IntakeFormProps {
  selectedRole: Role;
  onBack: () => void;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  }
};
export function IntakeForm({
  selectedRole,
  onBack
}: IntakeFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    institution: '',
    interest: 'treatment-strategies'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const updateField = (field: string, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  if (submitted) {
    return <div className="flex-1 flex items-center justify-center px-5 py-10" data-id="element-73">
        <motion.div className="w-full max-w-sm text-center" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-74">
          <div className="w-16 h-16 rounded-full bg-era-teal-light flex items-center justify-center mx-auto mb-5" data-id="element-75">
            <CheckCircleIcon className="w-8 h-8 text-era-teal" data-id="element-76" />
          </div>
          <h2 className="text-xl font-bold text-clinical-text mb-2" data-id="element-77">
            Thank you!
          </h2>
          <p className="text-sm text-clinical-secondary leading-relaxed mb-6" data-id="element-78">
            We've received your request. You'll receive further information
            about evolving IgAN management strategies at{' '}
            <span className="font-semibold" data-id="element-79">{form.email}</span>.
          </p>
          <button onClick={onBack} className="text-xs font-semibold text-clinical-muted" data-id="element-80">
            Return to results
          </button>
        </motion.div>
      </div>;
  }
  return <div className="flex-1 flex items-center justify-center px-5 py-8" data-id="element-81">
      <motion.div className="w-full max-w-sm" variants={containerVariants} initial="hidden" animate="visible" data-id="element-82">
        <motion.div variants={itemVariants} className="mb-6" data-id="element-83">
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-clinical-muted mb-4" data-id="element-84">
            <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-85" />
            Back to results
          </button>
          <h2 className="text-xl font-bold text-clinical-text mb-1.5" data-id="element-86">
            Get further information
          </h2>
          <p className="text-xs text-clinical-secondary leading-relaxed" data-id="element-87">
            Leave your details and we'll send you the latest evidence and
            resources on IgAN management.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-3" data-id="element-88">
          <motion.div variants={itemVariants} data-id="element-89">
            <label className="block text-[11px] font-bold text-clinical-muted uppercase tracking-wider mb-1.5" data-id="element-90">
              Full name
            </label>
            <input type="text" required value={form.name} onChange={e => updateField('name', e.target.value)} placeholder="Dr. Jane Smith" className="w-full bg-white border-2 border-gray-100 rounded-xl px-4 py-3 text-sm text-clinical-text placeholder:text-clinical-muted/50 focus:outline-none focus:border-era-red transition-colors" data-id="element-91" />
          </motion.div>

          <motion.div variants={itemVariants} data-id="element-92">
            <label className="block text-[11px] font-bold text-clinical-muted uppercase tracking-wider mb-1.5" data-id="element-93">
              Email
            </label>
            <input type="email" required value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="jane.smith@hospital.eu" className="w-full bg-white border-2 border-gray-100 rounded-xl px-4 py-3 text-sm text-clinical-text placeholder:text-clinical-muted/50 focus:outline-none focus:border-era-red transition-colors" data-id="element-94" />
          </motion.div>

          <motion.div variants={itemVariants} data-id="element-95">
            <label className="block text-[11px] font-bold text-clinical-muted uppercase tracking-wider mb-1.5" data-id="element-96">
              Institution
            </label>
            <input type="text" value={form.institution} onChange={e => updateField('institution', e.target.value)} placeholder="University Hospital (optional)" className="w-full bg-white border-2 border-gray-100 rounded-xl px-4 py-3 text-sm text-clinical-text placeholder:text-clinical-muted/50 focus:outline-none focus:border-era-red transition-colors" data-id="element-97" />
          </motion.div>

          <motion.div variants={itemVariants} data-id="element-98">
            <label className="block text-[11px] font-bold text-clinical-muted uppercase tracking-wider mb-1.5" data-id="element-99">
              I'm interested in
            </label>
            <select value={form.interest} onChange={e => updateField('interest', e.target.value)} className="w-full bg-white border-2 border-gray-100 rounded-xl px-4 py-3 text-sm text-clinical-text focus:outline-none focus:border-era-red transition-colors appearance-none" data-id="element-100">
              <option value="treatment-strategies" data-id="element-101">
                Evolving treatment strategies
              </option>
              <option value="clinical-evidence" data-id="element-102">
                Latest clinical evidence
              </option>
              <option value="guidelines" data-id="element-103">Updated guidelines & pathways</option>
              <option value="all" data-id="element-104">All of the above</option>
            </select>
          </motion.div>

          <motion.div variants={itemVariants} className="pt-2" data-id="element-105">
            <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-era-red active:bg-era-red-dark text-white font-bold px-5 py-3.5 rounded-2xl transition-colors duration-150 text-sm" data-id="element-106">
              <SendIcon className="w-4 h-4" data-id="element-107" />
              Send me information
            </button>
          </motion.div>

          <motion.p variants={itemVariants} className="text-[10px] text-clinical-muted text-center leading-relaxed pt-1" data-id="element-108">
            Your data is handled in accordance with ERA privacy policy. You can
            unsubscribe at any time.
          </motion.p>
        </form>
      </motion.div>
    </div>;
}